import React, { ChangeEvent, FC, useCallback } from 'react';
import { FormGroup, FormText, Input, Label, Table } from 'reactstrap';
import { Order, OrderItem } from '../../shared/orders/model';
import { useTranslation } from '../translations';
import { displayName } from '../utils';

export interface RefundGrantList {
  orderItemId: number;
  quantity: number;
}

interface RefundGrantTableProps {
  readonly order: Order;
  readonly list: RefundGrantList[];
  readonly setList: (list: RefundGrantList[]) => void;
}

export const RefundGrantTable: FC<RefundGrantTableProps> = ({ order, list, setList }) => {
  const { ts } = useTranslation();

  // Crafts an object with parentOrderItemId as the key for a list of addons to then be rendered underneath the primary item
  const addonMap = order.orderItems
    .filter(({ referenceId, parentOrderItemId }) => referenceId && parentOrderItemId)
    .reduce((map, orderItem) => {
      let existing = map.get(orderItem.parentOrderItemId!);
      if (!existing) {
        existing = [];
        map.set(orderItem.parentOrderItemId!, existing);
      }

      existing.push(orderItem);
      return map;
    }, new Map<number, OrderItem[]>());

  const renderOrderItems = order.orderItems
    .filter(({ referenceId, parentOrderItemId }) => referenceId && !parentOrderItemId)
    .map((orderItem) => (
      <RefundOrderItemTableItem
        addons={addonMap.get(orderItem.id)}
        key={orderItem.id}
        list={list}
        orderItem={orderItem}
        setList={setList}
      />
    ));

  if (renderOrderItems.length === 0) {
    return null;
  }

  return (
    <div>
      <p>{ts('refund_grant_table.remove_grants_given_to_the')}</p>
      <Table striped>
        <thead>
          <tr>
            <th>{ts('common.product_name')}</th>
            <th>{ts('common.quantity')}</th>
            <th>{ts('refund_grant_table.quantity_to_remove')}</th>
          </tr>
        </thead>
        <tbody>{renderOrderItems}</tbody>
      </Table>
      <FormGroup style={{ marginLeft: '7px' }}>
        <FormGroup check className="mt-10">
          <Input defaultChecked id="stockReturn" name="stockReturn" type="checkbox" />
          <Label check for="stockReturn">
            {ts('refund_grant_table.return_limited_quantity_items_to')}
          </Label>
        </FormGroup>
        <FormText color="muted">{ts('common.if_you_dont_want_to')}</FormText>
      </FormGroup>
    </div>
  );
};

interface RefundOrderItemTableItemProps {
  readonly orderItem: OrderItem;
  readonly list: RefundGrantList[];
  readonly addons?: OrderItem[];
  readonly setList: (list: RefundGrantList[]) => void;
}

const RefundOrderItemTableItem: FC<RefundOrderItemTableItemProps> = ({
  orderItem,
  list,
  addons,
  setList,
}) => {
  const { ts } = useTranslation();

  const onChange = useCallback(
    (a: ChangeEvent<HTMLInputElement>) => {
      const quantity = a.currentTarget.valueAsNumber;
      const item: RefundGrantList = list.find((t) => t.orderItemId === orderItem.id) ?? {
        orderItemId: orderItem.id,
        quantity,
      };

      item.quantity = quantity;

      setList([...list.filter((t) => t.orderItemId !== orderItem.id), item]);
    },
    [orderItem, list, setList],
  );

  return (
    <tr>
      <td>
        {displayName(orderItem.product)}
        {addons?.map((addon) => (
          <div key={addon.id} style={{ paddingLeft: '10px' }}>
            <small>
              {ts('addon')}: {displayName(addon.product)}
            </small>
          </div>
        ))}
      </td>
      <td>
        {orderItem.quantity}
        {addons?.map((addon) => (
          <div key={addon.id}>
            <small>{addon.quantity}</small>
          </div>
        ))}
      </td>
      <td>
        <Input
          id={`removeGrant${orderItem.id}`}
          max={orderItem.grant?.quantity ?? 1}
          min="0"
          onChange={onChange}
          step="1"
          type="number"
        />
      </td>
    </tr>
  );
};
